import addNoibuAttributesInCheckoutShipping from "@MasterBluePrint/Handlers/AddNoibuAttributesInCheckoutShippingHandler";
import setIsCheckoutShippingSubmitting from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/Handlers/SetIsCheckoutShippingSubmitting";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCountriesDataView, getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCountries";
import { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    loadCountries,
    setIsCheckoutShippingSubmitting,
    addNoibuAttributes: makeHandlerChainAwaitable<Parameters<typeof addNoibuAttributesInCheckoutShipping>[0], boolean>(
        addNoibuAttributesInCheckoutShipping,
    ),
};


const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        cartId: state.pages.checkoutShipping.cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        countries: getCurrentCountries(state),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        countriesDataView,
        billingAddress:state.pages.checkoutShipping.billingAddressFormState?.address,
        shippingAddress:state.pages.checkoutShipping.shippingAddressFormState?.address,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
   
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
    }

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!this.props?.countriesDataView?.value) {
            this.props.loadCountries({
                onComplete: () => {
                    this.submitForm();
                }
            });
            
        } else {
          await this.submitForm();
        }
    };

    submitForm = async () => {
        const validators: Validator[] = [];

        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });

        // Flag the customer-initiated submit so the chain-injected Piyovi address validation
        // runs here but not on page load / SSR (SetInitialValues runs the same chain).
        this.props.setIsCheckoutShippingSubmitting({ isCheckoutShippingSubmitting: true });
        let results: boolean[];
        try {
            results = await Promise.all(validators.map(validator => validator()));
        } finally {
            this.props.setIsCheckoutShippingSubmitting({ isCheckoutShippingSubmitting: false });
        }
        const isFormValid = results.every(result => result);
    
        if (isFormValid) {
            this.props.addNoibuAttributes(({
                billingAddress:this.props.billingAddress,
                shippingAddress:this.props.shippingAddress
            }));
            this.setState({ isRedirecting: true });
             // TODO ISC-12556
             await this.props.updateCart({});
             const continueUrl = this.props.cartId
                 ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                 : this.props.reviewAndSubmitPageLink!.url;
             this.props.history.push(continueUrl);
        } else {
            scrollToFirstFormError();
        }
    };

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <LoadingOverlay loading={this.props.isUpdatingCustomer || this.state.isRedirecting || this.props.isPreloadingData}>
                    <CheckoutShippingFormContext.Provider value={this.state}>
                        <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                            <Zone zoneName="Content" contentId={this.props.id} />
                        </form>
                    </CheckoutShippingFormContext.Provider>
                </LoadingOverlay>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
